import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import classNames from "classnames";
import { EntityValue, Page } from "@/model";
import { useDeleteEntityValue } from "@/query/entityValue";
import { editingEntityValueAtom } from "@/atom/editing";
import { useAgentId, useDialog, useSelection } from "@/hook";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { IndeterminateCheckbox } from "@/component/input";
import HighlightText from "@/component/HighlightText";
import { errorToast, successToast } from "@/component/Toast";
import SelectionActionBar from "@/component/SelectionActionBar";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";

import { EntityValuePageParam } from "../EntityValueView";


interface EntityValueTableProps {
    entityId: string;
    page?: Page<EntityValue>;
    pageParam: EntityValuePageParam;
}

const EntityValueTable: React.FC<EntityValueTableProps> = (props) => {
    const { entityId, page, pageParam } = props;
    
    const intl = useIntl();
    const agentId = useAgentId();
    const dialog = useDialog();

    const [editingEntityValue, setEditingEntityValue] = useAtom(editingEntityValueAtom);
    const { mutateAsync: deleteEntityValue } = useDeleteEntityValue(agentId, entityId);

    const pageIds = useMemo(() => page?.content.map(entityValue => entityValue.id) ?? [], [page?.content]);

    const { selectionCount, isAllSelected, pageSelectionStatus, selectedIds, markOne, markPage, markAll, clearSelection } = useSelection({
        targetIds: pageIds,
        total: page?.total ?? 0
    });

    const deleteByIds = async (ids: string[]) => {
        await deleteEntityValue({ ids: ids })
            .then(() => {
                if (editingEntityValue?.id && ids.includes(editingEntityValue.id)) {
                    setEditingEntityValue(undefined);
                }
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const deleteAll = async () => {
        await deleteEntityValue({ keyword: pageParam.searchKeyword })
            .then(() => {
                setEditingEntityValue(undefined);
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const onCheckEntityValue = (id: string, checked: boolean) => {
        if (checked) {
            !selectedIds.includes(id) && markOne(id, true);
        } else {
            markOne(id, false);
        }
    };

    const onClickUpdate = (id: string) => {
        setEditingEntityValue(page?.content.find(entityValue => entityValue.id === id));
    };

    const onClickDeleteItem = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();

        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i05041" }),
            content: intl.formatMessage({ id: "i05042" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: () => deleteByIds([id])
        });
    };

    const onClickDeleteSelection = (e: React.MouseEvent) => {
        e.preventDefault();

        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i05039" }),
            content: intl.formatMessage({ id: "i05040" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: () => isAllSelected ? deleteAll() : deleteByIds(selectedIds)
        });
    };

    return (
        <div role="table"
             className="selection-table grid-table-entity-value"
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     className="selection-table-th"
                >
                        <IndeterminateCheckbox status={pageSelectionStatus}
                                               onChange={checked => markPage(checked)}
                        />
                    </div>
                    <div role="columnheader"
                         className="selection-table-th"
                    >
                        {intl.formatMessage({ id: "i05034" })}
                    </div>
                    <div role="columnheader"
                         className="selection-table-th pl-[15px]"
                    >
                        {intl.formatMessage({ id: "i05035" })}
                    </div>
                    <div role="columnheader"
                         className="selection-table-th pl-[15px]"
                    >
                        {intl.formatMessage({ id: "i05036" })}
                    </div>
                <div role="columnheader"
                     className="selection-table-th"
                />
            </div>
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {page?.content.map((entityValue) => (
                    <div key={entityValue.id}
                         role="row"
                         className="selection-table-tr group"
                    >
                        <div role="cell"
                             className="selection-table-td"
                        >
                            <input type="checkbox"
                                    className={classNames(
                                        selectedIds.length ? "opacity-100" : "opacity-0",
                                        "selection-table-checkbox group-interaction"
                                    )}
                                    checked={selectedIds.includes(entityValue.id)}
                                    onChange={e => onCheckEntityValue(entityValue.id, e.target.checked)}
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td"
                        >
                            <HighlightText query={entityValue.name}
                                           keyword={pageParam.searchKeyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[15px]"
                        >
                            <HighlightText query={entityValue.synonyms.join(", ")}
                                           keyword={pageParam.searchKeyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td pl-[15px]"
                        >
                            {entityValue.mapping_value}
                        </div>
                        <div role="cell"
                             className="selection-table-td"
                        >
                            <Popover>
                                {({ open, close }) => (
                                    <>
                                        <PopoverButton className="selection-table-popover-btn"
                                                       open={open}
                                                       close={close}
                                        />
                                        <PopoverMenu>
                                            <PopoverItem className="selection-table-popover-item"
                                                         onClick={() => onClickUpdate(entityValue.id)}
                                            >
                                                <EditIcon className="size-3.5" />
                                                {intl.formatMessage({ id: "i05037" })}
                                            </PopoverItem>
                                            <PopoverItem className="selection-table-popover-item text-point-red"
                                                         onClick={e => onClickDeleteItem(e, entityValue.id)}
                                            >
                                                <DeleteIcon className="size-3.5" />
                                                {intl.formatMessage({ id: "i05038" })}
                                            </PopoverItem>
                                        </PopoverMenu>
                                    </>
                                )}
                            </Popover>
                        </div>
                    </div>
                ))}
                {!page?.content?.length && (
                    <div className="empty-table">
                        {intl.formatMessage({ id: pageParam.searchKeyword ? "b0014" : "b0018" })}
                    </div>
                )}
            </div>

            <SelectionActionBar selectionCount={selectionCount}
                                selectAll={() => markAll(true)}
                                deSelect={() => markAll(false)}
                                remove={onClickDeleteSelection}
            />
        </div>
    );
};


export default EntityValueTable;
